/**
 * Accessibility Overlay Definitions
 *
 * Defines the overlay system for filtering/highlighting POIs based on
 * accessibility attributes. Key insight: POI attributes ≠ overlay membership.
 * One attribute can trigger multiple overlays.
 */

export type OverlayCategory = "accessibility" | "sensory" | "practical";

export interface OverlaySubFilter {
  slug: string;
  label: string;
  icon?: string;
}

export interface OverlayDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: OverlayCategory;
  badgeColor: string;
  /** Attribute keys that match this overlay (OR logic) */
  matchesAttributes: string[];
  /** Whether to dim POIs that don't match when this overlay is active */
  dimNonMatching: boolean;
  /** Sub-filters for array attributes (e.g., dietary options) */
  subFilters?: OverlaySubFilter[];
  /** Whether this overlay affects wayfinding (future) */
  affectsWayfinding: boolean;
  /** Wayfinding constraints to apply (future) */
  wayfindingConstraints?: string[];
}

/**
 * All available overlays for V1.
 * Priority order (for badge stacking) is array index order.
 */
export const OVERLAY_DEFINITIONS: OverlayDefinition[] = [
  // Priority 1: Wheelchair (most specific)
  {
    id: "wheelchair",
    name: "Wheelchair Accessible",
    description: "Locations with wheelchair access",
    icon: "wheelchair",
    category: "accessibility",
    badgeColor: "#3b82f6", // blue-500
    matchesAttributes: [
      "wheelchair_accessible",
      "wheelchair_restroom",
      "wheelchair_rental",
      "ramp_available",
      "wide_entrance",
      "elevator",
    ],
    dimNonMatching: true,
    affectsWayfinding: true,
    wayfindingConstraints: ["avoid_stairs", "prefer_ramps"],
  },
  // Priority 2: Mobility (broader)
  {
    id: "mobility",
    name: "Mobility Assistance",
    description: "Rest areas, rentals, and accessible paths",
    icon: "person-cane",
    category: "accessibility",
    badgeColor: "#8b5cf6", // violet-500
    matchesAttributes: [
      "wheelchair_accessible",
      "wheelchair_restroom",
      "wheelchair_rental",
      "scooter_rental",
      "rest_area",
      "elevator",
      "ramp_available",
    ],
    dimNonMatching: false,
    affectsWayfinding: true,
    wayfindingConstraints: ["prefer_rest_stops"],
  },
  // Priority 3: Family
  {
    id: "family",
    name: "Family Friendly",
    description: "Changing stations, nursing rooms, kid-friendly areas",
    icon: "baby",
    category: "practical",
    badgeColor: "#f59e0b", // amber-500
    matchesAttributes: [
      "changing_station",
      "nursing_room",
      "family_restroom",
      "stroller_parking",
      "kid_friendly",
    ],
    dimNonMatching: false,
    affectsWayfinding: true,
    wayfindingConstraints: ["avoid_stairs"],
  },
  // Priority 4: Sensory Friendly
  {
    id: "sensory",
    name: "Sensory Friendly",
    description: "Quiet zones and sensory break rooms",
    icon: "ear-deaf",
    category: "sensory",
    badgeColor: "#ec4899", // pink-500
    matchesAttributes: [
      "quiet_zone",
      "sensory_break_room",
      "hearing_loop",
      "braille_signage",
    ],
    dimNonMatching: false,
    affectsWayfinding: false,
  },
  // Priority 5: Dietary Options
  {
    id: "dietary",
    name: "Dietary Options",
    description: "Food locations with dietary accommodations",
    icon: "utensils",
    category: "practical",
    badgeColor: "#22c55e", // green-500
    matchesAttributes: ["dietary_options"],
    dimNonMatching: true,
    subFilters: [
      { slug: "vegetarian", label: "Vegetarian", icon: "leaf" },
      { slug: "vegan", label: "Vegan", icon: "seedling" },
      { slug: "gluten_free", label: "Gluten-free", icon: "wheat-awn-circle-exclamation" },
      { slug: "halal", label: "Halal", icon: "moon" },
      { slug: "kosher", label: "Kosher", icon: "star-of-david" },
    ],
    affectsWayfinding: false,
  },
  // Priority 6: Service Animals
  {
    id: "service-animals",
    name: "Service Animals",
    description: "Relief areas and water stations for service animals",
    icon: "dog",
    category: "practical",
    badgeColor: "#10b981", // emerald-500
    matchesAttributes: ["service_animal_relief", "pet_water_station"],
    dimNonMatching: false,
    affectsWayfinding: false,
  },
  // Priority 7: Quiet Zones
  {
    id: "quiet",
    name: "Quiet Zones",
    description: "Low-stimulation areas",
    icon: "volume-xmark",
    category: "sensory",
    badgeColor: "#6366f1", // indigo-500
    matchesAttributes: ["quiet_zone", "sensory_break_room"],
    dimNonMatching: false,
    affectsWayfinding: false,
  },
  // Priority 8: Shaded Areas
  {
    id: "shaded",
    name: "Shaded Areas",
    description: "Covered and shaded locations",
    icon: "umbrella-beach",
    category: "practical",
    badgeColor: "#64748b", // slate-500
    matchesAttributes: ["shaded_area", "covered_area"],
    dimNonMatching: false,
    affectsWayfinding: false,
  },
];

/**
 * Get overlay definition by ID
 */
export function getOverlayById(id: string): OverlayDefinition | undefined {
  return OVERLAY_DEFINITIONS.find((o) => o.id === id);
}

/**
 * Get all overlays in a category
 */
export function getOverlaysByCategory(
  category: OverlayCategory
): OverlayDefinition[] {
  return OVERLAY_DEFINITIONS.filter((o) => o.category === category);
}

/**
 * Badge priority order (lower index = higher priority for stacking)
 */
export function getOverlayPriority(overlayId: string): number {
  const index = OVERLAY_DEFINITIONS.findIndex((o) => o.id === overlayId);
  return index === -1 ? Infinity : index;
}
